/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 📄 File: hooks/useSipPhone.ts
 🧩 Description:
    Custom React hook for WebRTC SIP phone functionality using SIP.js.
    Delegates:
    - Public types        → sipPhone/sipPhone.types.ts
    - DTMF utilities      → sipPhone/sipDtmfUtils.ts
    - SDP hold modifiers  → sipPhone/sipSdpModifiers.ts
    - ICE server builder  → sipPhone/sipConnectionUtils.ts
────────────────────────────────────────────────────────────────*/

import { useState, useRef, useCallback, useEffect } from 'react';
import {
    UserAgent, Registerer, Inviter, Session,
    SessionState, RegistererState, Invitation,
} from 'sip.js';

export type { PhoneStatus, CallState, SipPhoneConfig, UseSipPhoneReturn } from './sipPhone/sipPhone.types';
import type { PhoneStatus, CallState, CallInfo, SipPhoneConfig, UseSipPhoneReturn } from './sipPhone/sipPhone.types';
import { playDTMFTone } from './sipPhone/sipDtmfUtils';
import { HOLD_MODIFIERS, UNHOLD_MODIFIERS } from './sipPhone/sipSdpModifiers';
import { buildIceServers } from './sipPhone/sipConnectionUtils';

export function useSipPhone(): UseSipPhoneReturn {
    const [status, setStatus] = useState<PhoneStatus>('disconnected');
    const [callState, setCallState] = useState<CallState>('idle');
    const [callInfo, setCallInfo] = useState<CallInfo | null>(null);
    const [incomingCall, setIncomingCall] = useState<Invitation | null>(null);
    const [isMuted, setIsMuted] = useState(false);
    const [isOnHold, setIsOnHold] = useState(false);
    const [hasHeldCall, setHasHeldCall] = useState(false);
    const [isInConference, setIsInConference] = useState(false);
    const [conferenceRoomId, setConferenceRoomId] = useState<string | null>(null);

    const userAgentRef = useRef<UserAgent | null>(null);
    const registererRef = useRef<Registerer | null>(null);
    const sessionRef = useRef<Session | null>(null);
    const heldSessionRef = useRef<Session | null>(null);
    const heldCallInfoRef = useRef<CallInfo | null>(null);
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const iceGatheringTimeoutRef = useRef<number>(500);
    const autoAnswerRef = useRef<boolean>(false);

    // ── Remote audio element ──────────────────────────────────
    useEffect(() => {
        audioRef.current = new Audio();
        audioRef.current.autoplay = true;
        return () => { audioRef.current?.pause(); audioRef.current = null; };
    }, []);

    // ── Ringtone management ───────────────────────────────────
    const ringtoneAudioRef = useRef<HTMLAudioElement | null>(null);
    const ringtoneUrlRef = useRef<string>('/switchboard/audio/mixkit-vintage-telephone-ringtone-1356.wav');
    const ringtoneUnlockedRef = useRef<boolean>(false);
    const startRingtoneRef = useRef<() => void>(() => { });
    const stopRingtoneRef = useRef<() => void>(() => { });

    // Unlock audio context on first user gesture
    useEffect(() => {
        const unlockAudio = () => {
            if (ringtoneUnlockedRef.current) return;
            if (!ringtoneAudioRef.current) ringtoneAudioRef.current = new Audio();
            const audio = ringtoneAudioRef.current;
            audio.src = ringtoneUrlRef.current || '/audio/mixkit-vintage-telephone-ringtone-1356.wav';
            audio.volume = 0.001;
            const p = audio.play();
            if (p !== undefined) {
                p.then(() => {
                    audio.pause(); audio.currentTime = 0; audio.volume = 0.5;
                    ringtoneUnlockedRef.current = true;
                    document.removeEventListener('click', unlockAudio);
                    document.removeEventListener('keydown', unlockAudio);
                    document.removeEventListener('touchstart', unlockAudio);
                }).catch(() => { });
            }
        };
        document.addEventListener('click', unlockAudio);
        document.addEventListener('keydown', unlockAudio);
        document.addEventListener('touchstart', unlockAudio);
        return () => {
            document.removeEventListener('click', unlockAudio);
            document.removeEventListener('keydown', unlockAudio);
            document.removeEventListener('touchstart', unlockAudio);
        };
    }, []);

    const stopRingtone = useCallback(() => {
        if (ringtoneAudioRef.current) {
            const a = ringtoneAudioRef.current;
            a.pause(); a.currentTime = 0; a.oncanplaythrough = null; a.onerror = null; a.src = '';
            ringtoneAudioRef.current = null;
        }
    }, []);

    const startRingtone = useCallback(() => {
        stopRingtone();
        const url = ringtoneUrlRef.current;
        if (!url || url === 'none') return;
        try {
            const audio = new Audio();
            ringtoneAudioRef.current = audio;
            audio.loop = true; audio.volume = 0.5;
            audio.oncanplaythrough = () => { if (ringtoneAudioRef.current === audio) audio.play().catch(() => { }); };
            audio.onerror = null;
            audio.src = url; audio.load();
        } catch { /* silent */ }
    }, [stopRingtone]);

    startRingtoneRef.current = startRingtone;
    stopRingtoneRef.current = stopRingtone;

    // ── Session handlers ──────────────────────────────────────
    const setupSessionHandlers = useCallback((session: Session, direction: 'inbound' | 'outbound') => {
        sessionRef.current = session;
        session.stateChange.addListener((state: SessionState) => {
            switch (state) {
                case SessionState.Establishing:
                    setCallState(direction === 'outbound' ? 'outgoing' : 'ringing');
                    break;
                case SessionState.Established: {
                    setCallState('active');
                    setCallInfo((prev) => prev ? { ...prev, startTime: Date.now() } : null);
                    const remoteStream = new MediaStream();
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const receivers = (session as any).sessionDescriptionHandler?.peerConnection?.getReceivers();
                    receivers?.forEach((r: RTCRtpReceiver) => { if (r.track) remoteStream.addTrack(r.track); });
                    if (audioRef.current) audioRef.current.srcObject = remoteStream;
                    break;
                }
                case SessionState.Terminated:
                    stopRingtoneRef.current();
                    setCallState('idle'); setCallInfo(null); setIncomingCall(null);
                    setIsOnHold(false); setIsMuted(false);
                    sessionRef.current = null;
                    if (audioRef.current) audioRef.current.srcObject = null;
                    break;
            }
        });
    }, []);

    // ── Connect / Register ────────────────────────────────────
    const connect = useCallback(async (config: SipPhoneConfig) => {
        if (userAgentRef.current) return;
        setStatus('connecting');
        iceGatheringTimeoutRef.current = config.iceGatheringTimeout || 500;
        autoAnswerRef.current = config.autoAnswer || false;
        ringtoneUrlRef.current = config.ringtoneUrl || '/audio/mixkit-vintage-telephone-ringtone-1356.wav';
        try {
            const uri = UserAgent.makeURI(`sip:${config.extension}@${config.domain}`);
            if (!uri) throw new Error('Failed to create SIP URI');

            const userAgent = new UserAgent({
                uri,
                transportOptions: { server: config.wsServer },
                authorizationUsername: config.extension,
                authorizationPassword: config.password,
                logLevel: 'error', logConfiguration: false, logBuiltinEnabled: false,
                delegate: {
                    onInvite: (invitation: Invitation) => {
                        const remoteNumber = invitation.remoteIdentity.uri.user || 'Unknown';
                        const remoteName = invitation.remoteIdentity.displayName;
                        setIncomingCall(invitation);
                        setCallState('ringing');
                        setCallInfo({ direction: 'inbound', remoteNumber, remoteName });
                        setupSessionHandlers(invitation, 'inbound');
                        startRingtoneRef.current();
                        if (autoAnswerRef.current) {
                            setTimeout(async () => {
                                try {
                                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                    await invitation.accept({ sessionDescriptionHandlerOptions: { constraints: { audio: true, video: false }, iceGatheringTimeout: iceGatheringTimeoutRef.current } as any });
                                } catch (err) { console.error('[SIP] Auto-answer failed:', err); }
                            }, 500);
                        }
                    },
                },
                sessionDescriptionHandlerFactoryOptions: {
                    constraints: { audio: true, video: false },
                    peerConnectionConfiguration: { iceServers: buildIceServers(config), iceCandidatePoolSize: 0 },
                },
            });

            await userAgent.start();
            userAgentRef.current = userAgent;

            const registerer = new Registerer(userAgent);
            registererRef.current = registerer;
            registerer.stateChange.addListener((state: RegistererState) => {
                if (state === RegistererState.Registered) setStatus('registered');
                else if (state === RegistererState.Unregistered) setStatus('disconnected');
            });
            await registerer.register();
        } catch (err) {
            console.error('[SIP] Connection error:', err);
            setStatus('error');
        }
    }, [setupSessionHandlers]);

    // ── Disconnect ────────────────────────────────────────────
    const disconnect = useCallback(() => {
        registererRef.current?.unregister();
        userAgentRef.current?.stop();
        userAgentRef.current = null; registererRef.current = null;
        setStatus('disconnected');
    }, []);

    useEffect(() => () => { disconnect(); }, [disconnect]);

    // ── Call ──────────────────────────────────────────────────
    const call = useCallback(async (destination: string) => {
        if (!userAgentRef.current || status !== 'registered') return;
        const domain = userAgentRef.current.configuration.uri.host;
        const targetUri = UserAgent.makeURI(`sip:${destination}@${domain}`);
        if (!targetUri) return;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const inviter = new Inviter(userAgentRef.current, targetUri, { delegate: { onReject: (r: any) => console.error('[SIP] Rejected:', r.message?.statusCode) } as any });
        setCallState('outgoing'); setCallInfo({ direction: 'outbound', remoteNumber: destination });
        setupSessionHandlers(inviter, 'outbound');
        try {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            await inviter.invite({ sessionDescriptionHandlerOptions: { constraints: { audio: true, video: false }, iceGatheringTimeout: iceGatheringTimeoutRef.current } as any });
        } catch (err) { console.error('[SIP] Invite error:', err); setCallState('idle'); setCallInfo(null); }
    }, [status, setupSessionHandlers]);

    // ── Answer ────────────────────────────────────────────────
    const answer = useCallback(async () => {
        stopRingtone();
        if (!incomingCall) return;
        try {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            await incomingCall.accept({ sessionDescriptionHandlerOptions: { constraints: { audio: true, video: false }, iceGatheringTimeout: iceGatheringTimeoutRef.current } as any });
        } catch (err) { console.error('[SIP] Answer error:', err); }
        setIncomingCall(null);
    }, [incomingCall, stopRingtone]);

    // ── Hangup / Reject ───────────────────────────────────────
    const hangup = useCallback(() => {
        if (!sessionRef.current) return;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (sessionRef.current.state === SessionState.Established) (sessionRef.current as any).bye?.();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        else (sessionRef.current as any).cancel?.();
    }, []);

    const reject = useCallback(() => {
        stopRingtone();
        if (!incomingCall) return;
        incomingCall.reject(); setIncomingCall(null); setCallState('idle'); setCallInfo(null);
    }, [incomingCall, stopRingtone]);

    // ── Hold / Unhold ─────────────────────────────────────────
    const muteLocalTracks = (session: RTCPeerConnection | undefined, enabled: boolean) => {
        session?.getSenders().forEach((s) => { if (s.track?.kind === 'audio') s.track.enabled = enabled; });
    };

    const hold = useCallback(async () => {
        if (!sessionRef.current || callState !== 'active') return;
        try {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const session = sessionRef.current as any;
            if (typeof session.invite === 'function') await session.invite({ sessionDescriptionHandlerModifiers: HOLD_MODIFIERS });
            muteLocalTracks(session.sessionDescriptionHandler?.peerConnection, false);
            heldSessionRef.current = sessionRef.current;
            heldCallInfoRef.current = callInfo;
            setHasHeldCall(true);
            sessionRef.current = null;
            setIsOnHold(true); setCallState('hold');
        } catch (err) {
            console.error('[SIP] Hold error:', err);
            setIsOnHold(true); setCallState('hold');
        }
    }, [callState, callInfo]);

    const unhold = useCallback(async () => {
        if (heldSessionRef.current && !sessionRef.current) {
            sessionRef.current = heldSessionRef.current;
            heldSessionRef.current = null; heldCallInfoRef.current = null; setHasHeldCall(false);
        }
        if (!sessionRef.current || (callState !== 'hold' && !hasHeldCall)) return;
        try {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const session = sessionRef.current as any;
            if (typeof session.invite === 'function') await session.invite({ sessionDescriptionHandlerModifiers: UNHOLD_MODIFIERS });
            muteLocalTracks(session.sessionDescriptionHandler?.peerConnection, true);
            setIsOnHold(false); setCallState('active');
        } catch (err) {
            console.error('[SIP] Unhold error:', err);
            setIsOnHold(false); setCallState('active');
        }
    }, [callState, hasHeldCall]);

    // ── Swap ──────────────────────────────────────────────────
    const swap = useCallback(async () => {
        if (!heldSessionRef.current || !sessionRef.current) { console.error('[SIP] Swap requires both calls'); return; }
        try {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const active = sessionRef.current as any, held = heldSessionRef.current as any;
            if (typeof active.invite === 'function') await active.invite({ sessionDescriptionHandlerModifiers: HOLD_MODIFIERS });
            if (typeof held.invite === 'function') await held.invite({ sessionDescriptionHandlerModifiers: UNHOLD_MODIFIERS });
            heldSessionRef.current = active; sessionRef.current = held;
            heldCallInfoRef.current = callInfo; setCallInfo(heldCallInfoRef.current);
        } catch (err) { console.error('[SIP] Swap error:', err); }
    }, [callInfo]);

    // ── Mute ──────────────────────────────────────────────────
    const toggleMute = useCallback(() => {
        if (!sessionRef.current) return;
        try {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const pc = (sessionRef.current as any).sessionDescriptionHandler?.peerConnection as RTCPeerConnection;
            const newMuted = !isMuted;
            pc?.getSenders().forEach((s) => { if (s.track?.kind === 'audio') s.track.enabled = !newMuted; });
            setIsMuted(newMuted);
        } catch (err) { console.error('[SIP] Mute error:', err); }
    }, [isMuted]);

    // ── DTMF ──────────────────────────────────────────────────
    const sendDTMF = useCallback((digit: string) => {
        playDTMFTone(digit);
        if (!sessionRef.current || callState !== 'active') return;
        try {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const session = sessionRef.current as any;
            const pc = session.sessionDescriptionHandler?.peerConnection as RTCPeerConnection;
            const audioSender = pc?.getSenders().find((s) => s.track?.kind === 'audio');
            if (audioSender?.dtmf) {
                audioSender.dtmf.insertDTMF(digit, 160, 50);
            } else if (typeof session.info === 'function') {
                session.info({ requestOptions: { body: { contentDisposition: 'render', contentType: 'application/dtmf-relay', content: `Signal=${digit}\r\nDuration=160` } } });
            }
        } catch (err) { console.error('[SIP] DTMF error:', err); }
    }, [callState]);

    // ── Transfer ──────────────────────────────────────────────
    const transfer = useCallback(async (destination: string) => {
        if (!sessionRef.current || !userAgentRef.current) return;
        const domain = userAgentRef.current.configuration.uri.host;
        const targetUri = UserAgent.makeURI(`sip:${destination}@${domain}`);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (targetUri) await (sessionRef.current as any).refer?.(targetUri);
    }, []);

    // ── Attended Transfer ─────────────────────────────────────
    const attendedTransfer = useCallback(async () => {
        if (!heldSessionRef.current || !sessionRef.current) { console.error('[SIP] Attended transfer requires both calls'); return; }
        try {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const held = heldSessionRef.current as any;
            if (typeof held.refer === 'function') {
                await held.refer(sessionRef.current, { requestDelegate: { onAccept: () => console.log('[SIP] REFER accepted'), onReject: () => console.error('[SIP] REFER rejected') } });
                setTimeout(() => {
                    sessionRef.current = null; heldSessionRef.current = null; heldCallInfoRef.current = null;
                    setHasHeldCall(false); setCallState('idle'); setCallInfo(null);
                }, 1000);
            }
        } catch (err) { console.error('[SIP] Attended transfer error:', err); }
    }, []);

    // ── Conference ────────────────────────────────────────────
    const conference = useCallback(async () => {
        if (!heldSessionRef.current || !sessionRef.current || !userAgentRef.current) { console.error('[SIP] Conference requires both calls'); return; }
        try {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const active = sessionRef.current as any, held = heldSessionRef.current as any;
            const userAgent = userAgentRef.current;
            const domain = userAgent.configuration.uri.host;
            const roomId = Date.now().toString().slice(-6);
            const confRoomNumber = `*9${roomId}`;
            const confUri = UserAgent.makeURI(`sip:${confRoomNumber}@${domain}`);
            if (!confUri) { console.error('[SIP] Failed to create conference URI'); return; }

            if (typeof held.invite === 'function') { try { await held.invite(); } catch { /* ignore */ } }
            await new Promise((r) => setTimeout(r, 300));

            const referOpts = (label: string) => ({ requestDelegate: { onAccept: () => console.log(`[SIP] ${label} REFER accepted`), onReject: () => console.error(`[SIP] ${label} REFER rejected`) } });
            if (typeof held.refer === 'function') await held.refer(confUri, referOpts('Held'));
            await new Promise((r) => setTimeout(r, 200));
            if (typeof active.refer === 'function') await active.refer(confUri, referOpts('Active'));

            heldSessionRef.current = null; heldCallInfoRef.current = null; setHasHeldCall(false);
            await new Promise((r) => setTimeout(r, 500));

            const inviter = new Inviter(userAgent, confUri);
            setupSessionHandlers(inviter, 'outbound');
            setConferenceRoomId(roomId); setIsInConference(true);
            setCallState('outgoing'); setCallInfo({ remoteNumber: confRoomNumber, remoteName: `Conference ${roomId}`, direction: 'outbound', startTime: Date.now() });

            try {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                await inviter.invite({ sessionDescriptionHandlerOptions: { constraints: { audio: true, video: false }, iceGatheringTimeout: iceGatheringTimeoutRef.current } as any });
                sessionRef.current = inviter;
            } catch (err) { console.error('[SIP] Conference invite error:', err); setCallState('idle'); setCallInfo(null); setIsInConference(false); setConferenceRoomId(null); }
        } catch (err) { console.error('[SIP] Conference error:', err); }
    }, [setupSessionHandlers]);

    // ── Add to Conference ─────────────────────────────────────
    const addToConference = useCallback(async () => {
        if (!conferenceRoomId || !sessionRef.current || !userAgentRef.current) { console.error('[SIP] addToConference needs active conference'); return; }
        try {
            const domain = userAgentRef.current.configuration.uri.host;
            const confUri = UserAgent.makeURI(`sip:*9${conferenceRoomId}@${domain}`);
            if (!confUri) return;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const session = sessionRef.current as any;
            if (typeof session.refer === 'function') {
                await session.refer(confUri, { requestDelegate: { onAccept: () => console.log('[SIP] REFER accepted'), onReject: () => console.error('[SIP] REFER rejected') } });
            }
        } catch (err) { console.error('[SIP] Add to conference error:', err); }
    }, [conferenceRoomId]);

    // ── Auto-answer setter ────────────────────────────────────
    const setAutoAnswer = useCallback((value: boolean) => { autoAnswerRef.current = value; }, []);

    return {
        status, callState, callInfo, heldCallInfo: heldCallInfoRef.current,
        incomingCall, isMuted, isOnHold, hasHeldCall, isInConference, conferenceRoomId,
        connect, disconnect, call, answer, hangup, reject,
        hold, unhold, swap, toggleMute, sendDTMF, transfer,
        attendedTransfer, conference, addToConference, setAutoAnswer,
    };
}
